"use client";

import type {
  ChangeEvent,
  ChangeEventHandler,
  CompositionEventHandler,
  FocusEventHandler,
  FormEventHandler,
  ForwardedRef,
  InputHTMLAttributes,
  SyntheticEvent,
} from "react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import {
  type NumberConstraints,
  normalizeInputValue,
  syncNumberValidity,
} from "@/lib/forms/localized-input";

type TextControl = HTMLInputElement | HTMLTextAreaElement;

interface LocalizedInputOptions<T extends TextControl> {
  normalize?: (value: string) => string;
  nativeInputFallback?: boolean;
  browserOwned?: boolean;
  ref?: ForwardedRef<T>;
  numberConstraints?: NumberConstraints;
  value?: InputHTMLAttributes<HTMLInputElement>["value"];
  defaultValue?: InputHTMLAttributes<HTMLInputElement>["defaultValue"];
  onChange?: ChangeEventHandler<T>;
  onBlur?: FocusEventHandler<T>;
  onInput?: FormEventHandler<T>;
  onCompositionStart?: CompositionEventHandler<T>;
  onCompositionEnd?: CompositionEventHandler<T>;
}

// input/composition events carry the same input element. Preserve React's event
// methods explicitly: those methods are on its prototype, not enumerable props.
function toChangeEvent<T extends TextControl>(
  event: SyntheticEvent<T>,
): ChangeEvent<T> {
  return {
    ...event,
    target: event.currentTarget,
    preventDefault: () => event.preventDefault(),
    stopPropagation: () => event.stopPropagation(),
    isDefaultPrevented: () => event.isDefaultPrevented(),
    isPropagationStopped: () => event.isPropagationStopped(),
    persist: () => event.persist(),
  };
}

/** Keep native keyboard edits in sync even when React's change tracker misses one. */
export function useLocalizedInput<T extends TextControl>({
  normalize,
  nativeInputFallback = true,
  browserOwned = false,
  ref,
  numberConstraints,
  value,
  defaultValue,
  onChange,
  onBlur,
  onInput,
  onCompositionStart,
  onCompositionEnd,
}: LocalizedInputOptions<T>) {
  const composing = useRef(false);
  const nativeComposition = useRef(false);
  const lastEvent = useRef<Event | null>(null);
  const lastValue = useRef<string | null>(null);
  const inputRef = useRef<T | null>(null);
  const initialValue = useRef(value ?? defaultValue);
  const mergedRef = useCallback(
    (element: T | null) => {
      inputRef.current = element;
      if (element) lastValue.current = element.value;
      if (typeof ref === "function") return ref(element);
      if (ref) ref.current = element;
    },
    [ref],
  );
  const isNumber = numberConstraints !== undefined;
  const { min, max, step, required } = numberConstraints ?? {};
  // Form controls keep the browser's native editing buffer. React's controlled
  // value restoration can erase an IME edit before its input event arrives.
  // Apply actual form value changes (setValue/reset/edit defaults), never write
  // the old value just because an unrelated parent renders during composition.
  useLayoutEffect(() => {
    const element = inputRef.current;
    if (!browserOwned || !element || composing.current || value === undefined)
      return;
    const nextValue = String(normalizeInputValue(value ?? "", normalize));
    if (element.value !== nextValue) element.value = nextValue;
    lastValue.current = element.value;
  }, [browserOwned, value, normalize]);
  useEffect(() => {
    const element = inputRef.current;
    if (isNumber && element instanceof HTMLInputElement) {
      syncNumberValidity(element, { min, max, step, required });
      return () => element.setCustomValidity("");
    }
  }, [isNumber, min, max, step, required, value, defaultValue]);

  const notifyChange = (event: SyntheticEvent<T>) => {
    // React may deliver both onInput and onChange for one native input event.
    if (lastEvent.current === event.nativeEvent) return;
    lastEvent.current = event.nativeEvent;

    const element = event.currentTarget;
    const nativeComposing =
      "isComposing" in event.nativeEvent && event.nativeEvent.isComposing;
    if (nativeComposing && !composing.current) {
      nativeComposition.current = true;
      composing.current = true;
    } else if (nativeComposition.current && nativeComposing === false) {
      nativeComposition.current = false;
      composing.current = false;
    }
    if (normalize && !composing.current && !nativeComposing) {
      const raw = element.value;
      const normalized = normalize(raw);
      if (raw !== normalized) {
        const start = element.selectionStart;
        const end = element.selectionEnd;
        element.value = normalized;
        if (start !== null && end !== null) {
          element.setSelectionRange(
            normalize(raw.slice(0, start)).length,
            normalize(raw.slice(0, end)).length,
          );
        }
      }
    }
    lastValue.current = element.value;
    if (numberConstraints && element instanceof HTMLInputElement) {
      syncNumberValidity(element, numberConstraints);
    }
    onChange?.(toChangeEvent(event));
  };

  return {
    valueProps: browserOwned
      ? { defaultValue: normalizeInputValue(initialValue.current, normalize) }
      : {
          value: normalizeInputValue(
            value,
            composing.current ? undefined : normalize,
          ),
          defaultValue: normalizeInputValue(defaultValue, normalize),
        },
    inputEvents: {
      ref: mergedRef,
      onChange: (event: ChangeEvent<T>) => notifyChange(event),
      onBlur: (event: Parameters<FocusEventHandler<T>>[0]) => {
        composing.current = false;
        nativeComposition.current = false;
        const currentValue = event.currentTarget.value;
        if (
          nativeInputFallback &&
          (lastValue.current !== currentValue ||
            (normalize && normalize(currentValue) !== currentValue))
        ) {
          notifyChange(event);
        }
        onBlur?.(event);
      },
      onInput: (event: Parameters<FormEventHandler<T>>[0]) => {
        if (nativeInputFallback) notifyChange(event);
        onInput?.(event);
      },
      onCompositionStart: (
        event: Parameters<CompositionEventHandler<T>>[0],
      ) => {
        composing.current = true;
        nativeComposition.current = false;
        lastValue.current = event.currentTarget.value;
        onCompositionStart?.(event);
      },
      onCompositionEnd: (event: Parameters<CompositionEventHandler<T>>[0]) => {
        composing.current = false;
        nativeComposition.current = false;
        const value = event.currentTarget.value;
        // Normalize only after the IME commits. Also capture keyboards that emit
        // their final text on compositionend without a subsequent input event.
        if (
          lastValue.current !== value ||
          (normalize && normalize(value) !== value)
        ) {
          notifyChange(event);
        }
        onCompositionEnd?.(event);
      },
    },
  };
}
